import type { AccordionProps } from '@mui/material';
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { WarningsInterface } from '../store/simulation/types';
import React from 'react';
import type { GroupedWarnings } from '../util/warnings';
import {
  getSeverityColor,
  getSeverityTitle,
  groupWarningsBySeverity,
  totalWarningsCount,
} from '../util/warnings';
import WarningSeverityIndicatorComponent from './WarningSeverityIndicator';
import WarningsBadgeComponent from './WarningsBadge';
import { useMetricType } from '../hooks/useMetricType';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.grey['200']}`,
  overflow: 'hidden',
  borderRadius: '8px',
  '&:before': {
    display: 'none',
  },
}));

const GroupedWarningsList = ({ severity, messages }: GroupedWarnings) => {
  return (
    <Stack direction={'column'}>
      <Stack
        direction={'row'}
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <WarningSeverityIndicatorComponent severity={severity} />
        <Typography variant={'overline'}>
          {getSeverityTitle(severity)}
        </Typography>
      </Stack>
      <Stack direction={'column'} spacing={1} sx={{ marginLeft: 1 }}>
        {messages.map((message) => (
          <Stack
            key={message}
            direction={'row'}
            spacing={1}
            sx={{
              alignItems: 'baseline',
            }}
          >
            <Typography variant={'body2'}>•</Typography>
            <Typography variant={'body2'}>{message}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

type Props = {
  title?: string;
  defaultExpanded?: boolean;
  warnings: WarningsInterface | undefined;
};

const WarningsPanelAccordion = ({ defaultExpanded, warnings }: Props) => {
  const metricType = useMetricType();
  const warningItems = groupWarningsBySeverity(warnings, metricType);
  if (warningItems.length === 0) {
    return null;
  }

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Stack
          direction={'row'}
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
            }}
          >
            Warnings
          </Typography>
          <WarningsBadgeComponent
            color={getSeverityColor(warningItems[0].severity)}
            count={totalWarningsCount(warningItems)}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: 0 }}>
        <Stack direction='column' spacing={1}>
          {warningItems.map((warnings) => (
            <GroupedWarningsList
              key={warnings.severity}
              severity={warnings.severity}
              messages={warnings.messages}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default WarningsPanelAccordion;
